import { create } from "zustand";
import { setAutoTranslate } from "@/lib/autoTranslate";

const DICT = {
  en: {
    discover: "Discover", charts: "Charts", pricing: "Pricing", dashboard: "Dashboard", login: "Sign in", logout: "Sign out",
    search: "Search artists, tracks, albums…", heroTitle: "Independent music,", heroAccent: "amplified.",
    heroSub: "Step into a hub. Every genre has its own room, its own charts, its own rising stars.",
    hubs: "Hubs", topCharts: "Top Charts", trending: "Trending Tracks", spotlight: "Spotlight Artists", rising: "Rising Stars",
    freshDrops: "Fresh drops", follow: "Follow", following: "Following", tip: "Support / Tip", streams: "Streams",
    tracks: "Tracks", followers: "Followers", upgrade: "Upgrade", profile: "Profile", privacy: "Privacy", admin: "Admin",
    report: "Report copyright", nowPlaying: "Now playing", queue: "Queue", backToHubs: "All hubs", country: "Region",
    topCountries: "Top countries", topArtists: "Top artists", topTracks: "Top tracks", annual: "Annual", monthly: "Monthly",
    save20: "Save 20% · 2 months free", terms: "Terms of Service", privacyPolicy: "Privacy Policy", legal: "Legal Notices",
  },
  fr: {
    discover: "Découvrir", charts: "Classements", pricing: "Tarifs", dashboard: "Tableau de bord", login: "Connexion", logout: "Déconnexion",
    search: "Rechercher artistes, titres, albums…", heroTitle: "La musique indépendante,", heroAccent: "amplifiée.",
    heroSub: "Entrez dans un hub. Chaque genre a sa salle, ses classements et ses étoiles montantes.",
    hubs: "Hubs", topCharts: "Top classements", trending: "Titres tendance", spotlight: "Artistes à l'honneur", rising: "Étoiles montantes",
    freshDrops: "Nouveautés", follow: "Suivre", following: "Abonné", tip: "Soutenir l'artiste", streams: "Écoutes",
    tracks: "Titres", followers: "Abonnés", upgrade: "Passer Pro", profile: "Profil", privacy: "Confidentialité", admin: "Admin",
    report: "Signaler (droits d'auteur)", nowPlaying: "En lecture", queue: "File d'attente", backToHubs: "Tous les hubs", country: "Région",
    topCountries: "Pays en tête", topArtists: "Top artistes", topTracks: "Top titres", annual: "Annuel", monthly: "Mensuel",
    save20: "-20% · 2 mois offerts", terms: "CGU / CGV", privacyPolicy: "Politique RGPD", legal: "Mentions légales",
  },
  es: {
    discover: "Descubrir", charts: "Listas", pricing: "Precios", dashboard: "Panel", login: "Entrar", logout: "Salir",
    search: "Busca artistas, canciones, álbumes…", heroTitle: "Música independiente,", heroAccent: "amplificada.",
    heroSub: "Entra en un hub. Cada género tiene su sala, sus listas y sus estrellas emergentes.",
    hubs: "Hubs", topCharts: "Top listas", trending: "Tendencias", spotlight: "Artistas destacados", rising: "Estrellas emergentes",
    freshDrops: "Novedades", follow: "Seguir", following: "Siguiendo", tip: "Apoyar / Propina", streams: "Reproducciones",
    tracks: "Canciones", followers: "Seguidores", upgrade: "Mejorar", profile: "Perfil", privacy: "Privacidad", admin: "Admin",
    report: "Denunciar copyright", nowPlaying: "Sonando", queue: "Cola", backToHubs: "Todos los hubs", country: "Región",
    topCountries: "Países top", topArtists: "Top artistas", topTracks: "Top canciones", annual: "Anual", monthly: "Mensual",
    save20: "Ahorra 20% · 2 meses gratis", terms: "Términos", privacyPolicy: "Privacidad", legal: "Aviso legal",
  },
  de: {
    discover: "Entdecken", charts: "Charts", pricing: "Preise", dashboard: "Dashboard", login: "Anmelden", logout: "Abmelden",
    search: "Künstler, Tracks, Alben suchen…", heroTitle: "Unabhängige Musik,", heroAccent: "verstärkt.",
    heroSub: "Betritt einen Hub. Jedes Genre hat seinen Raum, seine Charts, seine Newcomer.",
    hubs: "Hubs", topCharts: "Top-Charts", trending: "Trend-Tracks", spotlight: "Spotlight-Künstler", rising: "Aufsteiger",
    freshDrops: "Neuheiten", follow: "Folgen", following: "Folge ich", tip: "Unterstützen", streams: "Streams",
    tracks: "Tracks", followers: "Follower", upgrade: "Upgrade", profile: "Profil", privacy: "Datenschutz", admin: "Admin",
    report: "Urheberrecht melden", nowPlaying: "Läuft gerade", queue: "Warteschlange", backToHubs: "Alle Hubs", country: "Region",
    topCountries: "Top-Länder", topArtists: "Top-Künstler", topTracks: "Top-Tracks", annual: "Jährlich", monthly: "Monatlich",
    save20: "20% sparen · 2 Monate gratis", terms: "AGB", privacyPolicy: "Datenschutz", legal: "Impressum",
  },
};

export const LANGS = ["en", "fr", "es", "de"];
export const useLang = create((set) => ({
  lang: localStorage.getItem("sonora_lang") || "en",
  setLang: (lang) => {
    localStorage.setItem("sonora_lang", lang);
    document.documentElement.lang = lang;
    set({ lang });
    setTimeout(() => setAutoTranslate(lang), 0);
  },
}));

if (typeof window !== "undefined") {
  document.documentElement.lang = useLang.getState().lang;
  window.addEventListener("DOMContentLoaded", () => setAutoTranslate(useLang.getState().lang));
  setTimeout(() => setAutoTranslate(useLang.getState().lang), 0);
}

export function useT() {
  const lang = useLang((s) => s.lang);
  return (k) => DICT[lang]?.[k] ?? DICT.en[k] ?? k;
}
